import React, { useState, useEffect, useRef } from "react";
import Images from "../images";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../Help/AxiosLogicInstance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Modal,
  Button,
  Form,
  Card,
  Container,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";

const ResetPassword = ({ show, handleClose, openSignIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get("userId");
  // States for showing/hiding passwords
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (location.pathname === "/reset-password" && userId) {
      setStep(2);
    }
  }, [userId, location.pathname]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Forgot Password: Send email
  const handleSendEmail = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting || isSubmittingRef.current) {
      toast.info("Request is being processed, please wait...");
      return;
    }

    setIsSubmitting(true);
    isSubmittingRef.current = true;

    try {
      const res = await axiosInstance.post(
        "/api/Student/StudentForgotPassword",
        {
          EmailId: formData.email,
        }
      );
      toast.success(
        res.data.Message || "Reset password link has been sent to your email."
      );
      // Close modal after sending email
      if (handleClose) {
        handleClose();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.Message ||
          "Failed to send reset password link. Please check your email."
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Set New Password
  const handleSetNewPassword = async (e) => {
    e.preventDefault();

    // First check for password match
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting || isSubmittingRef.current) {
      toast.info("Request is being processed, please wait...");
      return;
    }

    setIsSubmitting(true);
    isSubmittingRef.current = true;

    try {
    // In ResetPassword component, extract timestamp from URL
const params = new URLSearchParams(location.search);
const userId = params.get("userId");
const timestamp = params.get("timestamp"); // Add this line

// In handleSetNewPassword function, include timestamp in API call
const res = await axiosInstance.post(
  "/api/Student/StudentChangePassword3",
  {
    studentId: userId,
    Emailid: formData.email || "",
    CurrentPassword: "",
    ConfirmPassword: formData.confirmPassword,
    NewPassword: formData.newPassword,
    timestamp: timestamp, // Add timestamp to request body
  }
);
      if (res.data.Status === "200") {
        toast.success("Password reset successfully!");
        // Navigate to login page
        navigate("/");
      } else {
        toast.error(res.data.Message || "Password reset failed.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.Message || "Server error. Please try again."
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Standalone page component for direct URL access
  if (location.pathname === "/reset-password") {
    return (
      <div className="min-vh-100 d-flex align-items-center bgg-light">
        <Container fluid className="pt-5 mt-5 mb-2 pb-2">
          <Row className="justify-content-center">
            <Col xl={10} lg={12}>
              <Card
                className="border-0 shadow-sm overflow-hidden"
                style={{ borderRadius: "1rem" }}
              >
                <Row className="g-0">
                  {/* Left Image Panel */}
                  <Col lg={6} className="d-none d-lg-block position-relative">
                    <div className="h-100 position-relative reset-image">
                      {/* Gradient overlay */}
                      <div className="position-absolute top-0 start-0 w-100 h-100 gradient-black" />

                      {/* Welcome text overlay */}
                      <div
                        className="position-absolute top-50 start-50 translate-middle text-center text-white px-4"
                        style={{ zIndex: 2 }}
                      >
                        <h2 className="fw-bold mt-5">Reset Password</h2>
                      </div>
                    </div>
                  </Col>

                  {/* Right Form Panel */}
                  <Col lg={6} className="bg-white">
                    <div
                      className="p-4 p-md-5 d-flex flex-column justify-content-center"
                      style={{ minHeight: "500px" }}
                    >
                      {userId ? (
                        <>
                          {/* Header */}
                          <div className="text-center mb-2">
                            <div className="mb-3">
                              <div
                                className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center"
                                style={{ width: "60px", height: "60px" }}
                              >
                                <i className="fa fa-lock text-success fs-3"></i>
                              </div>
                            </div>
                            <h3 className="fw-bold text-dark mb-2">
                              Set New Password
                            </h3>
                            <p className="text-muted">
                              Enter your new password below
                            </p>
                          </div>

                          {/* Password reset form */}
                          <Form onSubmit={handleSetNewPassword}>
                            <Form.Group className="mb-2">
                              <Form.Label className="fw-semibold text-dark mb-2">
                                New Password{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type={showNew ? "text" : "password"}
                                  name="newPassword"
                                  className="py-3 border-end-0"
                                  placeholder="Enter new password"
                                  value={formData.newPassword}
                                  onChange={handleChange}
                                  required
                                  style={{
                                    fontSize: "1rem",
                                    boxShadow: "none",
                                    borderColor: "#e0e0e0",
                                  }}
                                />
                                <InputGroup.Text
                                  className="bg-white border-start-0 pe-3"
                                  role="button"
                                  onClick={() => setShowNew(!showNew)}
                                  style={{ borderColor: "#e0e0e0" }}
                                >
                                  <i
                                    className={`fa ${
                                      showNew ? "fa-eye-slash" : "fa-eye"
                                    } text-muted`}
                                  ></i>
                                </InputGroup.Text>
                              </InputGroup>
                            </Form.Group>

                            <Form.Group className="mb-4">
                              <Form.Label className="fw-semibold text-dark mb-2">
                                Confirm Password{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type={showConfirm ? "text" : "password"}
                                  name="confirmPassword"
                                  className="py-3 border-end-0"
                                  placeholder="Re-enter new password"
                                  value={formData.confirmPassword}
                                  onChange={handleChange}
                                  required
                                  style={{
                                    fontSize: "1rem",
                                    boxShadow: "none",
                                    borderColor: "#e0e0e0",
                                  }}
                                />
                                <InputGroup.Text
                                  className="bg-white border-start-0 pe-3"
                                  role="button"
                                  onClick={() => setShowConfirm(!showConfirm)}
                                  style={{ borderColor: "#e0e0e0" }}
                                >
                                  <i
                                    className={`fa ${
                                      showConfirm ? "fa-eye-slash" : "fa-eye"
                                    } text-muted`}
                                  ></i>
                                </InputGroup.Text>
                              </InputGroup>
                            </Form.Group>

                            <Button
                              type="submit"
                              variant="success"
                              disabled={isSubmitting}
                              className="w-100 py-3 fw-semibold reset-password-btn-primary"
                            >
                              {isSubmitting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-check-circle me-2"></i>
                                  Set Password
                                </>
                              )}
                            </Button>
                          </Form>
                        </>
                      ) : (
                        <>
                          {/* Invalid link message */}
                          <div className="text-center">
                            <div className="mb-4">
                              <div className="rounded-circle w-50 h-50 bg-danger bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3">
                                <i className="fa fa-exclamation-triangle text-danger fs-2"></i>
                              </div>
                            </div>
                            <h3 className="fw-bold text-dark mb-3">
                              Invalid Reset Link
                            </h3>
                            <p className="text-muted mb-4 fs-5">
                              The password reset link is invalid or has expired.
                              <br />
                              Please request a new password reset link.
                            </p>
                            <Button
                              variant="primary"
                              onClick={() => navigate("/")}
                              className="px-5 py-3 fw-semibold"
                            >
                              <i className="fa fa-home me-2"></i>
                              Back to Home
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Modal version for use within other components
  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={true}
    >
      <Modal.Body className="p-0">
        <Card className="border-0 overflow-hidden">
          <Row className="g-0">
            {/* Left Image Panel */}

            <Col lg={6} className="d-none d-lg-block position-relative">
              <div className="h-100 position-relative reset-image">
                {/* Gradient overlay */}
                <div className="position-absolute top-0 start-0 w-100 h-100 gradient-black" />

                {/* Welcome text overlay */}
                <div
                  className="position-absolute top-50 start-50 translate-middle text-center text-white px-4"
                  style={{ zIndex: 2 }}
                >
                  <h2 className="fw-bold mt-5">Forgot Password?</h2>
                </div>
              </div>
            </Col>

            {/* Right Form Panel */}
            <Col lg={6} className="bg-white">
              <div className="p-4 p-md-5 position-relative">
                {/* Close Button */}
                <Button
                  variant="light" // gives white background
                  className="position-absolute top-0 end-0 rounded-circle p-2 border-0 m-2 d-flex align-items-center justify-content-center shadow-sm"
                  onClick={handleClose}
                  style={{ width: "35px", height: "35px" }} // make it a perfect circle
                >
                  <i className="fa fa-times"></i>
                </Button>

                {/* Header */}
                <div className="text-center mb-4 mt-3">
                  <div className="mb-3">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center bgg-light bg-opacity-10"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <i className="fa fa-envelope text-success fs-4"></i>
                    </div>
                  </div>
                  <h4 className="fw-bold text-dark mb-2">Enter Your Email</h4>
                  <p className="text-muted small mb-0">
                    We'll send you a password reset link
                  </p>
                </div>

                {/* Email Form */}
                <Form onSubmit={handleSendEmail}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark mb-2">
                      Email Address <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-end-0">
                        <i className="fa fa-envelope text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        name="email"
                        size="sm"
                        className="py-3 border-start-0"
                        placeholder="Enter your registered email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        style={{
                          fontSize: "1rem",
                          boxShadow: "none",
                          borderColor: "#e0e0e0",
                        }}
                      />
                    </InputGroup>
                  </Form.Group>
                  <Row className="g-2 justify-content-center mt-3 p-2">
                    <Col
                      xs={12}
                      sm={6}
                      className="d-flex justify-content-center"
                    >
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          handleClose();
                          openSignIn();
                        }}
                        className="w-100 py-2 fw-semibold"
                        style={{ borderRadius: "0.5rem" }}
                      >
                        <i className="fa fa-arrow-left me-2"></i>
                        Back
                      </Button>
                    </Col>
                    <Col
                      xs={12}
                      sm={6}
                      className="d-flex justify-content-center"
                    >
                      <Button
                        type="submit"
                        size="sm"
                        variant="success"
                        className="w-100 py-2 fw-semibold"
                        style={{ borderRadius: "0.5rem" }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-paper-plane me-2"></i>
                            Send Link
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default ResetPassword;
